import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import './styles/kursusSider.css';
import ContactForm from '../components/contactForm';
import EventTilmeld from '../components/eventTilmeld';

function KursusSider() {
  const { kursusId } = useParams();
  const [kursus, setKursus] = useState(null);
  const [events, setEvents] = useState([]);
  const [showTilmeld, setShowTilmeld] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventTilmeldinger, setEventTilmeldinger] = useState({});

  useEffect(() => {
    const hentKursusOgEvents = async () => {
      // Find kursus ved hjælp af slug
      const kurserRef = collection(db, 'kurser');
      const q = query(kurserRef, where('slug', '==', kursusId));
      const kursusSnapshot = await getDocs(q);
      
      if (!kursusSnapshot.empty) {
        const kursusDoc = kursusSnapshot.docs[0];
        setKursus({ ...kursusDoc.data(), id: kursusDoc.id });
        
        // Hent events for dette kursus
        const eventsQuery = query(
          collection(db, 'events'), 
          where('kursusId', '==', kursusDoc.id)
        );
        const snapshot = await getDocs(eventsQuery);
        const idag = new Date();
        const kommendeEvents = snapshot.docs
          .map(doc => ({ ...doc.data(), id: doc.id }))
          .filter(event => {
            const eventDato = new Date(event.dato);
            return eventDato >= idag;
          })
          .sort((a, b) => new Date(a.dato) - new Date(b.dato));
        
        setEvents(kommendeEvents);
      }
    };

    hentKursusOgEvents();
  }, [kursusId]);

  useEffect(() => {
    const hentTilmeldinger = async () => {
      const tilmeldingerMap = {};
      for (const event of events) {
        const q = query(collection(db, 'tilmeldinger'), where('eventId', '==', event.id));
        const snapshot = await getDocs(q);
        const antalTilmeldte = snapshot.docs.reduce((sum, doc) => 
          sum + doc.data().antalDeltagere, 0);
        tilmeldingerMap[event.id] = antalTilmeldte;
      }
      setEventTilmeldinger(tilmeldingerMap);
    };

    if (events.length > 0) {
      hentTilmeldinger();
    }
  }, [events]);

  const handleTilmeldClick = (eventId) => {
    setSelectedEventId(eventId);
    setShowTilmeld(true);
  };

  const handleTilmeldingGennemført = async () => {
    // Genindlæs tilmeldinger for alle events
    const tilmeldingerMap = {};
    for (const event of events) {
      const q = query(collection(db, 'tilmeldinger'), where('eventId', '==', event.id));
      const snapshot = await getDocs(q);
      const antalTilmeldte = snapshot.docs.reduce((sum, doc) => 
        sum + doc.data().antalDeltagere, 0);
      tilmeldingerMap[event.id] = antalTilmeldte;
    }
    setEventTilmeldinger(tilmeldingerMap);
    setShowTilmeld(false);
  };

  if (!kursus) return <div>Indlæser...</div>;

  return (
    <>
      <div className="kursussider-hero split">
        <Link to="/kursus" className="kursussider-tilbage-knap">← Tilbage til kurser</Link>
        <div className="kursussider-hero-left">
          <div className="kursussider-hero-content">
            <h1>{kursus.navn}</h1>
            <p className="kursussider-hero-subtitle">{kursus.beskrivelse}</p>
          </div>
        </div>
        <div className="kursussider-hero-right">
          <img src={kursus.billedeUrl} alt={kursus.navn} className="kursussider-hero-image" />
        </div>
      </div>

      <section className="kursussider-content-section">
        <div className="kursussider-container">
          <h2>Kursusindhold</h2>
          <div 
            className="kursussider-intro-text"
            dangerouslySetInnerHTML={{ __html: kursus.indhold }}
          />
          
          <div className="kursussider-course-content">
            <h3>Kommende kursusdatoer:</h3>
            {!showTilmeld ? (
              <div className="kursussider-events-list">
                {events.map(event => {
                  const antalTilmeldte = eventTilmeldinger[event.id] || 0;
                  const ledigePladser = event.maxDeltagere - antalTilmeldte;
                  
                  return (
                    <div key={event.id} className="kursussider-event-item">
                      <div className="kursussider-event-details">
                        <p><strong>Dato:</strong> {event.dato}</p>
                        <p><strong>Tid:</strong> {event.tid}</p>
                        <p><strong>Lokation:</strong> {event.lokation}</p>
                        <p><strong>Pris pr. deltager:</strong> {event.pris} kr. ekskl. moms</p>
                        <p><strong>Tilmeldte:</strong> {antalTilmeldte}/{event.maxDeltagere}</p>
                      </div>
                      {ledigePladser > 0 ? (
                        <button onClick={() => handleTilmeldClick(event.id)}>
                          Tilmeld ({ledigePladser} pladser tilbage)
                        </button>
                      ) : (
                        <button disabled className="kursussider-event-fuldt">
                          Fuldt booket
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div>
                <button 
                  onClick={() => setShowTilmeld(false)} 
                  className="kursussider-tilbage-button"
                >
                  ← Tilbage til events
                </button>
                <EventTilmeld 
                  eventId={selectedEventId} 
                  onClose={() => setShowTilmeld(false)}
                  onTilmeldingGennemført={handleTilmeldingGennemført}
                  maxDeltagere={events.find(event => event.id === selectedEventId)?.maxDeltagere}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="kursussider-practical-info">
        <div className="kursussider-container">
          <h2>Praktisk information</h2>
          <div 
            dangerouslySetInnerHTML={{ __html: kursus.praktiskInfo }}
          />
        </div>
      </section>
    </>
  );
}

export default KursusSider;
